import React, { useCallback, useMemo } from 'react';
import { MessageList, ConversationSkeleton, PrototypeFixCard } from '@ideoz/ui';
import type { Message, MessageArtifact } from '@ideoz/core';
import { useConversation } from '../context/ConversationContext';
import type { ConversationFindings } from '../hooks/useConversationFindings';
import { turnBuildDidNotFinish } from '../../prototyper/utils/unfinishedTurn';
import { getWorkbenchTabId } from '../../prototyper/utils/versionTracker';
import { usePillTurnVersions } from '../../prototyper/usePillTurnVersions';
import {
  unfinishedBuildAction,
  versionHasNumber,
  versionSetAside,
} from '../../prototyper/utils/turnVersions';
import { unfinishedBuildButtons } from '../../prototyper/utils/prototypeVersionMenu';

/**
 * The message-display area of the conversation workspace (extracted from
 * `ConversationModule` in Phase 2, slice 2): the loading skeleton, the scroll
 * container, and the `MessageList` itself (with its inline-finding anchors and
 * live prototyper/workbench slots).
 *
 * Presentational. Everything on `facade.streaming`/`facade.headers` and the
 * workbench-stream values are read straight from `useConversation()`; only the
 * genuinely module-owned values (the findings hook instance, the scroll ref,
 * and the module's derived flags/handlers) arrive as props. The composer — and
 * with it the interactive-question card and the scroll button — stays a sibling
 * in the module, so `useScrollToBottom` also stays there and we only receive its
 * container ref here.
 */
// Prop types mirror the matching `MessageList` props (this pane forwards them
// straight through). They are declared locally rather than `Pick`ed from
// `MessageListProps` because `@ideoz/ui` resolves to its built declarations for
// the web project, and that surface doesn't re-export the props interface.
export interface MessagesPaneProps {
  /** The turn a pressed Continue is finishing: its pill shows as busy until that settles. */
  continuingTurnId?: string | null;
  /** What that busy pill says: "Finishing…" for Continue, "Starting…" for Build again. */
  continuingTurnLabel?: string;
  /** The single findings-state instance owned by ConversationModule. */
  findings: ConversationFindings;
  /** Scroll container ref — `useScrollToBottom` lives in the module (shared with the composer's scroll button). */
  conversationContainerRef: React.RefObject<HTMLDivElement>;
  /** While switching/loading, the messages are removed from layout and the skeleton shows. */
  showConversation: boolean;
  messages: Message[];
  projectId?: string | null;
  isLoading: boolean;
  isStreaming: boolean;
  isClarification: boolean;
  isReflection: boolean;
  isRepeated: boolean;
  isReadyToCreate: boolean;
  isPrototyper?: boolean;
  shouldShowBanner: boolean;
  isPrototypeOpen?: boolean;
  onPrototypeReturn?: () => void;
  onPrototyperClick?: (
    prototyperDemoLinkId?: string | number,
    // `continueBuild`: the pill's Continue button, on a build that stopped before it passed.
    // `didNotFinish`: the pill showed "Didn't finish" when it was clicked.
    // `conversationHasFinishedVersion`: added here, from the versions the pills read.
    meta?: {
      llmInteractionId?: string | null;
      continueBuild?: boolean;
      didNotFinish?: boolean;
      conversationHasFinishedVersion?: boolean;
    }
  ) => void;
  onStreamedArtifactOpen?: (artifact: MessageArtifact) => void;
  onSubmit: (text: string) => void;
  onFeedback?: (
    type: 'like' | 'dislike',
    messageId?: string | null,
    projectId?: string | null
  ) => void | Promise<void>;
  onRetryDrawing?: (drawType: string) => void;
}

/**
 * "Tell the AI what to change" on the fix card: the founder's next message starts a turn that
 * fixes the failed version with their words (the AI API sends the stored build log as repair).
 */
function focusConversationComposer(): void {
  document
    .querySelector<HTMLElement>(
      'textarea[data-testid="conversation-chat-textarea"], [data-testid="conversation-chat-textarea"] textarea'
    )
    ?.focus();
}

export function MessagesPane({
  continuingTurnId = null,
  continuingTurnLabel = 'Finishing…',
  findings,
  conversationContainerRef,
  showConversation,
  messages,
  projectId,
  isLoading,
  isStreaming,
  isClarification,
  isReflection,
  isRepeated,
  isReadyToCreate,
  isPrototyper,
  shouldShowBanner,
  isPrototypeOpen,
  onPrototypeReturn,
  onPrototyperClick,
  onStreamedArtifactOpen,
  onSubmit,
  onFeedback,
  onRetryDrawing,
}: MessagesPaneProps): JSX.Element {
  const { facade, workbench } = useConversation();

  const { turnVersions: workbenchTurnVersions, getWorkbenchSnapshotIndexEntry } = workbench;
  // Prototype turns, and whether each has settled: the key changes when a build ends here.
  const prototypeTurnsKey = messages
    .filter((m) => m.role === 'assistant' && m.prototypeReady === true && m.llmInteractionId)
    .map((m) => `${m.llmInteractionId}:${m.buildStatus ?? ''}`)
    .join('|');
  const pillTurnVersions = usePillTurnVersions({
    projectId: facade.state.currentProjectId,
    conversationId: facade.state.currentConversationId,
    enabled: workbench.isStreamingPrototype === true && prototypeTurnsKey !== '',
    refreshKey: `${prototypeTurnsKey}#${workbench.isWorkbenchBuildLive ? 'live' : 'idle'}`,
  });
  // The workbench's list once the panel loaded it (it follows lease and verify changes live);
  // until then, the pills' own.
  const turnVersions =
    workbenchTurnVersions?.status === 'ready' ? workbenchTurnVersions : pillTurnVersions;
  const isPrototypeBuildUnfinished = useCallback(
    (llmInteractionId: string) => {
      const message = messages.find(
        (m) => m.role === 'assistant' && m.llmInteractionId === llmInteractionId
      );
      if (!message) return false;
      return turnBuildDidNotFinish({
        message,
        version:
          turnVersions?.status === 'ready' ? turnVersions.get(llmInteractionId) : undefined,
        indexEntry: getWorkbenchSnapshotIndexEntry?.(llmInteractionId),
        ownerTabId: getWorkbenchTabId(),
      });
    },
    [messages, turnVersions, getWorkbenchSnapshotIndexEntry]
  );

  // Which unfinished builds get a button: the newest of each prototype, as in the versions menu.
  const unfinishedButtons = useMemo(
    () =>
      turnVersions?.status === 'ready'
        ? unfinishedBuildButtons({ messages, versions: turnVersions })
        : null,
    [messages, turnVersions]
  );

  // What the button on an unfinished build's card does: Continue, or Build again when the
  // build failed (Task 722); none on an older unfinished build of the same prototype.
  const prototypeBuildAction = useCallback(
    (llmInteractionId: string) => {
      if (turnVersions?.status !== 'ready') return undefined;
      if (unfinishedButtons?.get(llmInteractionId) === false) return 'none' as const;
      return unfinishedBuildAction(turnVersions.get(llmInteractionId)?.status);
    },
    [turnVersions, unfinishedButtons]
  );

  // A build the founder removed, or one a newer finished build replaced: its card says so.
  const prototypeBuildSetAside = useCallback(
    (llmInteractionId: string) =>
      turnVersions?.status === 'ready'
        ? versionSetAside(turnVersions.get(llmInteractionId)?.status)
        : undefined,
    [turnVersions]
  );

  // A pill carries a version number only once its turn has a finished version. Without the
  // versions (not loaded, or the API did not answer) every pill keeps its number, as before.
  const isPrototypeVersionSettled = useCallback(
    (llmInteractionId: string) => {
      if (turnVersions?.status !== 'ready') return true;
      const fromVersion = versionHasNumber(turnVersions.get(llmInteractionId));
      if (fromVersion !== undefined) return fromVersion;
      if (getWorkbenchSnapshotIndexEntry?.(llmInteractionId)?.buildVerified === true) return true;
      if (turnVersions.get(llmInteractionId)?.status === 'verified') return true;
      const message = messages.find(
        (m) => m.role === 'assistant' && m.llmInteractionId === llmInteractionId
      );
      return message?.buildStatus === 'success';
    },
    [messages, turnVersions, getWorkbenchSnapshotIndexEntry]
  );

  // The pill's number is the version's own, numbered per prototype by the backend when its build
  // finished; the turn's message carries it too, for when the versions are not loaded.
  const prototypeVersionNumber = useCallback(
    (llmInteractionId: string) =>
      turnVersions?.status === 'ready'
        ? (turnVersions.get(llmInteractionId)?.number ?? undefined)
        : undefined,
    [turnVersions]
  );

  // Whether any turn has a version the panel could show; an unfinished pill does not open the
  // panel onto an empty workspace when none has. Unknown (no versions yet) counts as yes, which is
  // the behaviour from before.
  const conversationHasFinishedVersion = useMemo(() => {
    if (turnVersions?.status !== 'ready') return true;
    return messages.some(
      (m) =>
        m.role === 'assistant' &&
        m.llmInteractionId != null &&
        (turnVersions.get(m.llmInteractionId)?.status === 'verified' ||
          getWorkbenchSnapshotIndexEntry?.(m.llmInteractionId)?.buildVerified === true)
    );
  }, [messages, turnVersions, getWorkbenchSnapshotIndexEntry]);
  const handlePrototyperClick = useCallback<NonNullable<MessagesPaneProps['onPrototyperClick']>>(
    (prototyperDemoLinkId, meta) =>
      onPrototyperClick?.(prototyperDemoLinkId, { ...meta, conversationHasFinishedVersion }),
    [onPrototyperClick, conversationHasFinishedVersion]
  );
  const { inlineFindingsByMessageId, resolvingFindingId, handleFindingAction } = findings;
  const fixAttention = workbench.prototypeFixAttention ?? null;
  const prototypeFixCard = fixAttention
    ? {
        llmInteractionId: fixAttention.llmInteractionId,
        node: (
          <PrototypeFixCard
            fixAttempts={fixAttention.fixAttempts}
            fixAttemptsGranted={fixAttention.fixAttemptsGranted}
            notice={fixAttention.notice}
            reason={fixAttention.reason ?? null}
            canUndo={Boolean(workbench.prototypeFixCanUndo)}
            busyAction={workbench.prototypeFixBusy ?? null}
            onTryAnotherFix={workbench.onTryAnotherFix}
            onUndo={workbench.onUndoPrototypeChange}
            onBuildAgain={workbench.onBuildAgainFromFix}
            onTellAi={focusConversationComposer}
            onLoadDetails={workbench.loadPrototypeFixDetails}
          />
        ),
      }
    : null;

  return (
    <>
      {/* Skeleton Loading */}
      {!showConversation && (
        <div className="conversation-container flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden justify-center w-full mb-4 [scrollbar-gutter:stable] scrollbar-chat px-3">
          <ConversationSkeleton messageCount={1} />
        </div>
      )}

      {/* Messages Container */}
      <div
        ref={conversationContainerRef}
        data-testid="chat-messages-container"
        className={`conversation-container flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden justify-center w-full mb-4 [scrollbar-gutter:stable] scrollbar-chat px-3 ${
          // While switching/loading, remove the previous
          // conversation from layout (not just opacity-0) so its
          // tall height + scrollbar don't linger under the
          // skeleton. It reappears once the new messages load.
          showConversation ? 'opacity-100' : 'hidden'
        }`}
      >
        <div className="min-w-0 max-w-full">
          <MessageList
            messages={messages}
            projectId={projectId}
            isLoading={isLoading}
            isStreaming={isStreaming}
            isClarification={isClarification || Boolean(facade.streaming.clarification)}
            clarificationOptions={facade.streaming.clarification?.options ?? []}
            streamProgress={facade.streaming.streamProgress}
            streamThoughts={facade.streaming.streamThoughts}
            hasStartedResponse={facade.streaming.hasStartedResponse}
            thinkingDurationMs={facade.streaming.thinkingDurationMs}
            isReflection={isReflection}
            isRepeated={isRepeated}
            isReadyToCreate={isReadyToCreate}
            isPrototyper={isPrototyper}
            shouldShowBanner={shouldShowBanner}
            onPrototypeReturn={onPrototypeReturn}
            onPrototyperClick={onPrototyperClick ? handlePrototyperClick : undefined}
            onStreamedArtifactOpen={onStreamedArtifactOpen}
            onSubmit={onSubmit}
            onFeedback={onFeedback}
            setIsReflection={facade.headers.setIsReflection}
            setIsRepeated={facade.headers.setIsRepeated}
            onRetryDrawing={onRetryDrawing}
            // Direction is decided per message from its own
            // content/detectedLanguage (MessageItem's
            // `computedIsRtl`). Passing a single conversation-
            // wide `forcedDirection` would flip every message
            // to the same direction — an Arabic reply and an
            // English reply must render rtl/ltr independently.
            isPrototypeOpen={isPrototypeOpen}
            streamingWorkbenchSlot={
              workbench.isStreamingPrototype && isPrototypeOpen
                ? workbench.workbenchToolActivity
                : undefined
            }
            streamingWorkbenchLlmInteractionId={workbench.streamingWorkbenchLlmInteractionId}
            livePrototyperBuildTimeline={workbench.livePrototyperBuildTimeline}
            isWorkbenchBuildLive={workbench.isWorkbenchBuildLive}
            workbenchTimelineTerminalVisible={workbench.workbenchTimelineTerminalVisible}
            activeSnapshotInteractionId={workbench.activeSnapshotInteractionId}
            restoreInteractionLoadingId={continuingTurnId ?? workbench.restoreInteractionLoadingId}
            restoreLoadingLabel={continuingTurnId ? continuingTurnLabel : undefined}
            isPrototypeBuildUnfinished={
              workbench.isStreamingPrototype ? isPrototypeBuildUnfinished : undefined
            }
            prototypeBuildSetAside={
              workbench.isStreamingPrototype ? prototypeBuildSetAside : undefined
            }
            prototypeBuildAction={workbench.buildAgain ? prototypeBuildAction : undefined}
            isPrototypeVersionSettled={
              workbench.isStreamingPrototype ? isPrototypeVersionSettled : undefined
            }
            prototypeVersionNumber={
              workbench.isStreamingPrototype ? prototypeVersionNumber : undefined
            }
            liveBuildLabel={workbench.workbenchAutoFixLabel ?? undefined}
            prototypeFixCard={prototypeFixCard}
            inlineFindingsByMessageId={inlineFindingsByMessageId}
            onFindingAction={(findingId, action) => {
              void handleFindingAction(findingId, action);
            }}
            resolvingFindingId={resolvingFindingId}
          />
        </div>
      </div>
    </>
  );
}

export default MessagesPane;
